import React from 'react';
import { Player } from '@remotion/player';
import { AbsoluteFill, interpolate, useCurrentFrame } from 'remotion';
import styled from 'styled-components';
import { v4 as uuid4 } from 'uuid';
import hiraganaData from 'kana-svg-data/dist/allHiragana.json';
import katakanaData from 'kana-svg-data/dist/allKatakana.json';
import {
  IKanjiVgKanaData,
  KANJIVG_KANA_DATA,
} from '@component/iyagigo/classroom/character-study/character-stroke-board/kanjivg-kana-data';

const FPS = 30;
const FIRST_STROKE_FRAME = 12;
const STROKE_FRAME_INTERVAL = 40;
const STROKE_DURATION_IN_FRAMES = 30;
const END_PADDING_IN_FRAMES = 45;
const BOARD_COLOR = '#294f42';
const STROKE_COLOR = '#fff8dc';
const NUMBER_COLOR = '#f3d88f';
const GUIDE_STROKE_COLOR = 'rgba(255, 255, 255, 0.13)';
const COMPOSITION_WIDTH = 320;
const COMPOSITION_HEIGHT = 180;

// 획순 오픈소스 후보:
// - KanjiVG: 가장 널리 쓰이는 중심선 기반 SVG (CC BY-SA 3.0)
// - AnimCJK: 글자 윤곽 + 획별 clipPath, 히라가나·가타카나 지원 (LGPL-3.0+)
// - StrokeSVG: Klee One 기반 가나 전용 SVG (SIL OFL 1.1 / MIT)
// 이 컴포넌트는 KanjiVG와 AnimCJK를 지원하며, 호출부의 source로 렌더러를 선택한다.
export type TCharacterStrokeSource = 'kanjivg' | 'animcjk';

interface IProps {
  character: string;
  source: TCharacterStrokeSource;
}

interface IKanjiVgCompositionProps extends Record<string, unknown> {
  data: IKanjiVgKanaData;
}

interface IAnimCjkPath {
  id: string;
  value: string;
}

interface IAnimCjkMedian {
  id: string;
  value: number[][];
}

interface IAnimCjkKanaData {
  charCode: number;
  strokes: IAnimCjkPath[];
  clipPaths: IAnimCjkPath[];
  medians: IAnimCjkMedian[];
}

interface IAnimCjkStrokePart {
  id: string;
  centerPath?: string;
}

interface IAnimCjkStrokeGroup {
  parts: IAnimCjkStrokePart[];
  numberPosition?: number[];
}

interface IAnimCjkCompositionProps extends Record<string, unknown> {
  data: IAnimCjkKanaData;
}

interface IStrokeNumberProps {
  number: number;
  x: number;
  y: number;
  opacity: number;
  radius: number;
  fontSize: number;
  strokeWidth: number;
}

interface IStrokePlayerProps<T extends Record<string, unknown>> {
  component: React.ComponentType<T>;
  inputProps: T;
  strokeCount: number;
}

const ANIM_CJK_KANA_DATA = new Map<number, IAnimCjkKanaData>(
  ([...hiraganaData, ...katakanaData] as IAnimCjkKanaData[]).map((data) => [data.charCode, data]),
);

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 10px;
  background: ${BOARD_COLOR};
`;

const ScreenReaderText = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const PathContainer = styled.g``;

const compositionStyle: React.CSSProperties = {
  alignItems: 'center',
  justifyContent: 'center',
  background: 'radial-gradient(circle at 52% 45%, rgba(79, 126, 102, 0.5), transparent 62%)',
};

const playerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  backgroundColor: 'transparent',
};

const getStrokeAnimation = (frame: number, strokeIndex: number) => {
  const startFrame = FIRST_STROKE_FRAME + strokeIndex * STROKE_FRAME_INTERVAL;
  const endFrame = startFrame + STROKE_DURATION_IN_FRAMES;
  const progress = interpolate(frame, [startFrame, endFrame], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const numberOpacity = interpolate(
    frame,
    [startFrame - 4, startFrame + 4, endFrame + 12, endFrame + 20],
    [0, 1, 1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  return { progress, numberOpacity };
};

const getDurationInFrames = (strokeCount: number) =>
  FIRST_STROKE_FRAME + strokeCount * STROKE_FRAME_INTERVAL + END_PADDING_IN_FRAMES;

const getLogicalStrokeId = (id: string) => id.match(/^\d+/)?.[0] ?? id;

const createAnimCjkStrokeGroups = (data: IAnimCjkKanaData): IAnimCjkStrokeGroup[] => {
  const centerPathById = new Map(data.clipPaths.map(({ id, value }) => [id, value]));
  const medianById = new Map(data.medians.map(({ id, value }) => [id, value]));
  const partsByStrokeId = new Map<string, IAnimCjkStrokePart[]>();

  data.strokes.forEach(({ id }) => {
    const strokeId = getLogicalStrokeId(id);
    const parts = partsByStrokeId.get(strokeId) ?? [];
    parts.push({ id, centerPath: centerPathById.get(id) });
    partsByStrokeId.set(strokeId, parts);
  });

  return Array.from(partsByStrokeId.values()).map((parts) => ({
    parts,
    numberPosition: medianById.get(parts[0].id)?.[0],
  }));
};

const StrokeNumber: React.FC<IStrokeNumberProps> = ({
  number,
  x,
  y,
  opacity,
  radius,
  fontSize,
  strokeWidth,
}) => (
  <g opacity={opacity}>
    <circle cx={x} cy={y} r={radius} fill={BOARD_COLOR} stroke={NUMBER_COLOR} strokeWidth={strokeWidth} />
    <text
      x={x}
      y={y}
      fill={NUMBER_COLOR}
      fontSize={fontSize}
      fontWeight={700}
      textAnchor='middle'
      dominantBaseline='central'>
      {number}
    </text>
  </g>
);

const KanjiVgStrokeOrderComposition: React.FC<IKanjiVgCompositionProps> = ({ data }) => {
  const frame = useCurrentFrame();

  return (
    <AbsoluteFill style={compositionStyle}>
      <svg viewBox='0 0 109 109' width='78%' height='88%' aria-label='가나 획순'>
        {data.strokes.map((path, index) => {
          const { progress, numberOpacity } = getStrokeAnimation(frame, index);
          const numberPosition = data.numberPositions[index];

          return (
            <PathContainer key={uuid4()}>
              <path
                d={path}
                pathLength={1}
                fill='none'
                stroke={STROKE_COLOR}
                strokeWidth={5}
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeDasharray={1}
                strokeDashoffset={1 - progress}
                opacity={progress === 0 ? 0 : 1}
              />
              {numberPosition && (
                <StrokeNumber
                  number={index + 1}
                  x={numberPosition.x - 6}
                  y={numberPosition.y - 3}
                  opacity={numberOpacity}
                  radius={8.5}
                  fontSize={12}
                  strokeWidth={1.5}
                />
              )}
            </PathContainer>
          );
        })}
      </svg>
    </AbsoluteFill>
  );
};

const AnimCjkStrokeOrderComposition: React.FC<IAnimCjkCompositionProps> = ({ data }) => {
  const frame = useCurrentFrame();
  const strokeGroups = React.useMemo(() => createAnimCjkStrokeGroups(data), [data]);

  return (
    <AbsoluteFill style={compositionStyle}>
      <svg viewBox='0 0 1024 1024' width='78%' height='88%' aria-label='가나 획순'>
        <defs>
          {data.strokes.map(({ id, value }) => (
            <clipPath key={uuid4()} id={`animcjk-${data.charCode}-${id}`}>
              <path d={value} />
            </clipPath>
          ))}
        </defs>
        {data.strokes.map(({ value }) => (
          <path key={uuid4()} d={value} fill={GUIDE_STROKE_COLOR} />
        ))}
        {strokeGroups.map(({ parts, numberPosition }, strokeIndex) => {
          const { progress, numberOpacity } = getStrokeAnimation(frame, strokeIndex);

          return (
            <PathContainer key={uuid4()}>
              {parts.map(({ id, centerPath }) =>
                centerPath ? (
                  <path
                    key={uuid4()}
                    d={centerPath}
                    pathLength={1}
                    fill='none'
                    stroke={STROKE_COLOR}
                    strokeWidth={128}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeDasharray={1}
                    strokeDashoffset={1 - progress}
                    clipPath={`url(#animcjk-${data.charCode}-${id})`}
                  />
                ) : null,
              )}
              {numberPosition && (
                <StrokeNumber
                  number={strokeIndex + 1}
                  x={numberPosition[0] - 95}
                  y={numberPosition[1]}
                  opacity={numberOpacity}
                  radius={56}
                  fontSize={82}
                  strokeWidth={10}
                />
              )}
            </PathContainer>
          );
        })}
      </svg>
    </AbsoluteFill>
  );
};

function StrokePlayer<T extends Record<string, unknown>>({
  component,
  inputProps,
  strokeCount,
}: IStrokePlayerProps<T>) {
  return (
    <Player
      component={component}
      inputProps={inputProps}
      durationInFrames={getDurationInFrames(strokeCount)}
      compositionWidth={COMPOSITION_WIDTH}
      compositionHeight={COMPOSITION_HEIGHT}
      fps={FPS}
      autoPlay
      loop
      controls={false}
      clickToPlay={false}
      spaceKeyToPlayOrPause={false}
      acknowledgeRemotionLicense
      style={playerStyle}
    />
  );
}

const CharacterStrokeBoard: React.FC<IProps> = ({ character, source }) => {
  const animCjkData = ANIM_CJK_KANA_DATA.get(character.codePointAt(0) ?? 0);
  const kanjiVgData = KANJIVG_KANA_DATA[character];
  if ((source === 'animcjk' && !animCjkData) || (source === 'kanjivg' && !kanjiVgData)) return null;

  return (
    <Wrapper aria-label={`${character} 획순 애니메이션`}>
      <ScreenReaderText>{character}</ScreenReaderText>
      {source === 'animcjk' && animCjkData && (
        <StrokePlayer
          key={`${source}-${character}`}
          component={AnimCjkStrokeOrderComposition}
          inputProps={{ data: animCjkData }}
          strokeCount={new Set(animCjkData.strokes.map(({ id }) => getLogicalStrokeId(id))).size}
        />
      )}
      {source === 'kanjivg' && kanjiVgData && (
        <StrokePlayer
          key={`${source}-${character}`}
          component={KanjiVgStrokeOrderComposition}
          inputProps={{ data: kanjiVgData }}
          strokeCount={kanjiVgData.strokes.length}
        />
      )}
    </Wrapper>
  );
};

export default CharacterStrokeBoard;
